import {
  ArrowLeftOutline24Icon,
  ArrowRightOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { format, intlFormat, isToday, isTomorrow, isYesterday } from "date-fns";
import { VehicleDate } from "features/VehicleSidebar/VehicleList/AddVehicles/useVehicleDate";
import styled from "styled-components";
import { Controller, UseFormReturn } from "react-hook-form";
import { toTime } from "@internationalized/date";
import { Text } from "shared/components/typography/TitleStyles";
import { IconButton, TimePicker } from "shared/components/Spor";
import { AddReserveAlternativeTransportRequest } from "shared/types/alternativeTransport";

const ChangeDayContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 100px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colorTextSecondary};
  color: ${({ theme }) => theme.colorTextMain};
  height: 54px;
  min-width: max-content;
  gap: 3px;
  padding: 1px;
`;

const getFormattedText = (date: Date): string => {
  const formattedDateString = format(date, "dd.MM");
  if (isYesterday(date)) return `i går, ${formattedDateString}`;
  if (isToday(date)) return `i dag, ${formattedDateString}`;
  if (isTomorrow(date)) return `i morgen, ${formattedDateString}`;

  return `${intlFormat(
    date,
    { weekday: "long" },
    { locale: "nb-NO" },
  )}, ${formattedDateString}`;
};

type Props = {
  showChangeDayOptions: boolean;
  vehicleDate: VehicleDate;
};

export const DayPicker = ({ showChangeDayOptions, vehicleDate }: Props) =>
  showChangeDayOptions ? (
    <ChangeDayContainer>
      <IconButton
        aria-label="Gå en dag bak"
        variant="ghost"
        icon={<ArrowLeftOutline24Icon />}
        onClick={vehicleDate.goToPreviousDay}
        disabled={!vehicleDate.canGoToPreviousDay}
      />
      <Text>{getFormattedText(vehicleDate.selectedDate)}</Text>
      <IconButton
        aria-label="Gå en dag frem"
        variant="ghost"
        icon={<ArrowRightOutline24Icon />}
        onClick={vehicleDate.goToNextDay}
        disabled={!vehicleDate.canGoToNextDay}
      />
    </ChangeDayContainer>
  ) : null;

export const TimePickerController = ({
  name,
  label,
  formMethods,
}: {
  name: "shiftStartTime" | "shiftEndTime";
  label: string;
  formMethods: UseFormReturn<AddReserveAlternativeTransportRequest>;
}) => (
  <Controller
    control={formMethods.control}
    name={name}
    render={({ field }) => (
      <TimePicker
        label={label}
        minuteInterval={30}
        value={toTime(field.value)}
        onValueChange={(time) => {
          if (!time) return;

          const newValue = field.value.set({
            hour: time.hour,
            minute: time.minute,
            second: time.second ?? 0,
          });

          if (name === "shiftStartTime") {
            const currentEndTime = formMethods.getValues("shiftEndTime");

            if (currentEndTime.compare(newValue) <= 0) {
              const newEndTime = newValue.add({ minutes: 30 });
              formMethods.setValue("shiftEndTime", newEndTime, {
                shouldValidate: true,
              });
            }
          }

          if (name === "shiftEndTime") {
            const currentStartTime = formMethods.getValues("shiftStartTime");

            if (newValue.compare(currentStartTime) <= 0) {
              return;
            }
          }

          field.onChange(newValue);
        }}
      />
    )}
  />
);
